import { useState, useCallback } from "react";
import { ledgerService } from "../api/ledgerService";
import { useToast } from "../context/ToastContext";
import { Ledger, LedgerTransaction, LedgerReportSummary, ReversalRequest, LedgerPosting, LedgerReportParams, PackagePostingReq, ReversalActionReq } from "../models/ledger.types";
// import { ledgerService } from "../services/ledgerService";
// import {
//   Ledger,
//   LedgerPosting,
//   LedgerTransaction,
//   LedgerReportSummary,
//   LedgerReportParams,
//   PackagePostingReq,
//   ReversalRequest,
//   ReversalActionReq,
// } from "../types/ledger.types";
// import { useToast } from "../contexts/ToastContext";

export const useLedger = () => {
  const [ledgers, setLedgers] = useState<Ledger[]>([]);
  const [transactions, setTransactions] = useState<LedgerTransaction[]>([]);
  const [summary, setSummary] = useState<LedgerReportSummary | null>(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const { showSuccess, showError } = useToast();
  const [reversalRequests, setReversalRequests] = useState<ReversalRequest[]>(
    []
  );

  const fetchLedgers = useCallback(async () => {
    setLoading(true);
    try {
      const data = await ledgerService.getAllLedgers();
      setLedgers(data);
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Failed to fetch ledgers";
      showError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [showError]);

  const postLedgerAccount = useCallback(
    async (posting: LedgerPosting): Promise<boolean> => {
      setLoading(true);
      try {
        const resp = await ledgerService.postLedgerAccount(posting);
        const debitedLedger = resp.data?.drName;
        const creditedLedger = resp.data?.crName;
        showSuccess(
          `${debitedLedger} debited successfully. ${creditedLedger} credited successfully`
        );
        return true;
      } catch (error: any) {
        const errorMessage =
          error?.response?.data?.message || "Failed to post ledger account";
        showError(errorMessage);
        return false;
      } finally {
        setLoading(false);
      }
    },
    [showSuccess, showError]
  );

  const getPostingReport = useCallback(
    async (params: LedgerReportParams): Promise<boolean> => {
      setLoading(true);
      try {
        const dataResponse = await ledgerService.getPostingReport(params);

        // Sort by date ascending for proper running balance calculation
        const sortedData = [...dataResponse].sort(
          (a, b) =>
            new Date(a.dateCreated).getTime() -
            new Date(b.dateCreated).getTime()
        );

        setTransactions(sortedData);

        // Calculate summary
        const totalDebit = sortedData.reduce(
          (sum, item) => sum + (parseFloat(String(item.dr)) || 0),
          0
        );
        const totalCredit = sortedData.reduce(
          (sum, item) => sum + (parseFloat(String(item.cr)) || 0),
          0
        );
        const netBalance = totalCredit - totalDebit; // Cr - Dr

        // Calculate final running balance
        let calculatedRunningBalance = 0;
        sortedData.forEach((item) => {
          const credit = parseFloat(String(item.cr)) || 0;
          const debit = parseFloat(String(item.dr)) || 0;
          calculatedRunningBalance += credit - debit;
        });

        setSummary({
          totalDebit,
          totalCredit,
          netBalance,
          transactionCount: sortedData.length,
          runningBalance: calculatedRunningBalance,
        });

        return true;
      } catch (error: any) {
        const errorMessage =
          error?.response?.data?.message || "Failed to fetch report data";
        showError(errorMessage);
        return false;
      } finally {
        setLoading(false);
      }
    },
    [showError]
  );

  const getAllPostingReport = useCallback(
    async (params: LedgerReportParams): Promise<boolean> => {
      setLoading(true);
      try {
        const dataResponse = await ledgerService.getAllPostingReport(params);

        // Sort by date ascending for proper running balance calculation
        const sortedData = [...dataResponse].sort(
          (a, b) =>
            new Date(a.dateCreated).getTime() -
            new Date(b.dateCreated).getTime()
        );

        setTransactions(sortedData);

        // Calculate summary
        const totalDebit = sortedData.reduce(
          (sum, item) => sum + (parseFloat(String(item.dr)) || 0),
          0
        );
        const totalCredit = sortedData.reduce(
          (sum, item) => sum + (parseFloat(String(item.cr)) || 0),
          0
        );
        const netBalance = totalCredit - totalDebit; // Cr - Dr

        // Calculate final running balance
        let calculatedRunningBalance = 0;
        sortedData.forEach((item) => {
          const credit = parseFloat(String(item.cr)) || 0;
          const debit = parseFloat(String(item.dr)) || 0;
          calculatedRunningBalance += credit - debit;
        });

        setSummary({
          totalDebit,
          totalCredit,
          netBalance,
          transactionCount: sortedData.length,
          runningBalance: calculatedRunningBalance,
        });

        return true;
      } catch (error: any) {
        const errorMessage =
          error?.response?.data?.message || "Failed to fetch report data";
        showError(errorMessage);
        return false;
      } finally {
        setLoading(false);
      }
    },
    [showError]
  );

  const postPackageAccount = useCallback(
    async (posting: PackagePostingReq): Promise<boolean> => {
      setLoading(true);
      try {
        await ledgerService.postPackageAccount(posting);
        showSuccess("Package posting created successfully");
        return true;
      } catch (error: any) {
        const errorMessage =
          error?.response?.data?.message || "Failed to post package account";
        showError(errorMessage);
        return false;
      } finally {
        setLoading(false);
      }
    },
    [showSuccess, showError]
  );

  const getPostingsByPostCode = useCallback(
    async (postCode: string): Promise<boolean> => {
      setLoading(true);
      try {
        const dataResponse = await ledgerService.getPostingsByPostCode(
          postCode
        );
        setTransactions(dataResponse);
        return true;
      } catch (error: any) {
        const errorMessage =
          error?.response?.data?.message ||
          "Failed to fetch postings by post code";
        showError(errorMessage);
        return false;
      } finally {
        setLoading(false);
      }
    },
    [showError]
  );

  const initiateReversal = useCallback(
    async (postCode: string): Promise<boolean> => {
      setLoading(true);
      try {
        await ledgerService.initiateReversal(postCode);
        showSuccess("Reversal initiated successfully");
        return true;
      } catch (error: any) {
        const errorMessage =
          error?.response?.data?.message || "Failed to initiate reversal";
        showError(errorMessage);
        return false;
      } finally {
        setLoading(false);
      }
    },
    [showSuccess, showError]
  );

  const getAllReversalRequests = useCallback(
    async (params?: {
      status?: string;
      startDate?: string;
      endDate?: string;
    }): Promise<boolean> => {
      setLoading(true);
      try {
        const dataResponse = await ledgerService.getAllReversalRequests(params);
        setReversalRequests(dataResponse);
        return true;
      } catch (error: any) {
        const errorMessage =
          error?.response?.data?.message || "Failed to fetch reversal requests";
        showError(errorMessage);
        return false;
      } finally {
        setLoading(false);
      }
    },
    [showError]
  );

  const approveReversal = useCallback(
    async (req: ReversalActionReq): Promise<boolean> => {
      setSubmitting(true);
      try {
        const resp = await ledgerService.approveReversal(req);
        if (resp.data?.status) {
          showSuccess("Reversal approved successfully");
          return true;
        } else {
          showError("Failed to approve reversal");
          return false;
        }
      } catch (error: any) {
        const errorMessage =
          error?.response?.data?.message || "Failed to approve reversal";
        showError(errorMessage);
        return false;
      } finally {
        setSubmitting(false);
      }
    },
    [showSuccess, showError]
  );

  const rejectReversal = useCallback(
    async (req: ReversalActionReq): Promise<boolean> => {
      setSubmitting(true);
      try {
        const resp = await ledgerService.rejectReversal(req);
        if (resp.data?.status) {
          showSuccess("Reversal rejected successfully");
          return true;
        } else {
          showError(`${resp.data?.message || "Failed to reject reversal"}`);
          return false;
        }
      } catch (error: any) {
        const errorMessage =
          error?.response?.data?.message || "Failed to reject reversal";
        showError(errorMessage);
        return false;
      } finally {
        setSubmitting(false);
      }
    },
    [showSuccess, showError]
  );

  return {
    ledgers,
    transactions,
    summary,
    loading,
    submitting,
    reversalRequests,
    fetchLedgers,
    postLedgerAccount,
    getPostingReport,
    getAllPostingReport,
    postPackageAccount,
    getPostingsByPostCode,
    initiateReversal,
    getAllReversalRequests,
    approveReversal,
    rejectReversal,
  };
};
